/**
 * React Query layer for study feature.
 * Language-dependent queries include i18n.language in key to refetch on language change.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { primeFileSignedUrlCache } from "@/api/services/global/file";
import { studyService } from "@/infrastructure/services";
import {
  TSubjectQuizAnswerRequest,
  TStudyVocabQuizAnswerRequest,
  TSubjectQuizPracticeAnswerRequest,
} from "@/infrastructure/adapters/study/study.types";

/** Query: Fetch study page data (recommendation + subjects) */
export const useStudyPageDataQuery = () => {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.PAGE_DATA, i18n.language],
    queryFn: () => studyService.getPageData(),
    staleTime: 0,
  });
};

/** Query: Fetch diagnosis quiz session for a subject (always Japanese) */
export const useDiagnosisQuizSessionQuery = (subjectId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.DIAGNOSIS_QUIZ, subjectId],
    queryFn: () => studyService.getDiagnosisQuizSession(subjectId),
    enabled: !!subjectId && enabled,
    retry: false,
    staleTime: Infinity,
  });
};

export const useDiagnosisQuizMetadataQuery = (subjectId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.DIAGNOSIS_QUIZ_METADATA, subjectId],
    queryFn: () => studyService.getDiagnosisQuizMetadata(subjectId),
    enabled: !!subjectId && enabled,
    retry: false,
    staleTime: 0,
  });
};

/** Query: Fetch word practice session for a subject.
 *  gcTime: 0 ensures cache is discarded on unmount so re-entering the page always fetches fresh words. */
export const useWordPracticeSessionQuery = (subjectId: string) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.WORD_PRACTICE, subjectId, i18n.language],
    queryFn: async () => {
      const session = await studyService.getWordPracticeSession(subjectId);
      // Hand BE-signed image URLs to the signed-url cache so flashcards skip the per-word API call
      primeFileSignedUrlCache(
        queryClient,
        session.words.map((w) => ({ filePath: w.imageFile ?? null, signedUrl: w.imageUrl ?? null })),
      );
      return session;
    },
    enabled: !!subjectId,
    gcTime: 0,
  });
};

/** Query: Fetch word quiz session for a subject */
export const useWordQuizSessionQuery = (subjectId: string) => {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.WORD_QUIZ, subjectId, i18n.language],
    queryFn: () => studyService.getWordQuizSession(subjectId),
    enabled: !!subjectId,
  });
};

/** Query: Fetch level practice session (Lv1-Lv5 練習) */
export const useLevelPracticeSessionQuery = (subjectId: string, level: number) => {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.LEVEL_PRACTICE, subjectId, level, i18n.language],
    queryFn: () => studyService.getLevelPracticeSession(subjectId, level),
    enabled: !!subjectId && level >= 1 && level <= 5,
  });
};

/** Query: Fetch text explanation session (テキスト解説) */
export const useTextExplanationSessionQuery = (subjectId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.TEXT_EXPLANATION, subjectId],
    queryFn: () => studyService.getTextExplanationSession(subjectId),
    enabled: !!subjectId,
  });
};

/** Query: Fetch learning recommendation (skipped when recommendation setting is off) */
export const useRecommendationQuery = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.RECOMMENDATION],
    queryFn: () => studyService.getRecommendation(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

/* ── Subject Quiz APIs ── */

/** Query: Fetch subject levels (API 1) */
export const useSubjectLevelsQuery = (subjectId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.SUBJECT_LEVELS, subjectId],
    queryFn: () => studyService.getSubjectLevels(subjectId),
    enabled: !!subjectId,
    staleTime: 5 * 60 * 1000,
  });
};

/** Mutation: Create subject quiz session (API 2) */
export const useCreateSubjectQuizSessionMutation = () => {
  return useMutation({
    mutationFn: ({ subjectId, level, limit }: { subjectId: string; level: string; limit: number }) =>
      studyService.createSubjectQuizSession(subjectId, level, limit),
  });
};

/** Mutation: Submit subject quiz answer (API 3) — invalidates recommendation + levels cache on completion */
export const useSubmitSubjectQuizAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quizSessionId, req }: { quizSessionId: string; req: TSubjectQuizAnswerRequest }) =>
      studyService.submitSubjectQuizAnswer(quizSessionId, req),
    onSuccess: (data) => {
      if (data.isCompleted) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY.RECOMMENDATION] });
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY.SUBJECT_LEVELS] });
    },
  });
};

/** Mutation: Update vocabulary progress after practice */
export const useUpdateVocabularyProgressMutation = () => {
  return useMutation({
    mutationFn: ({ subjectId, vocabularyId }: { subjectId: string; vocabularyId: number }) =>
      studyService.updateVocabularyProgress(subjectId, vocabularyId),
  });
};

/** Query: Fetch subject quiz result (API 4) */
export const useSubjectQuizResultQuery = (quizSessionId: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.SUBJECT_QUIZ_RESULT, quizSessionId],
    queryFn: () => studyService.getSubjectQuizResult(quizSessionId!),
    enabled: !!quizSessionId,
  });
};

/* ── Vocabulary Quiz APIs ── */

/** Mutation: Create vocabulary quiz session */
export const useCreateVocabularyQuizSessionMutation = () => {
  return useMutation({
    mutationFn: ({ subjectId, limit }: { subjectId: string; limit: number }) => studyService.createVocabularyQuizSession(subjectId, limit),
  });
};

/** Mutation: Submit vocabulary quiz answer — invalidates levels cache on completion */
export const useSubmitVocabularyQuizAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quizSessionId, req }: { quizSessionId: string; req: TStudyVocabQuizAnswerRequest }) =>
      studyService.submitVocabularyQuizAnswer(quizSessionId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY.SUBJECT_LEVELS] });
    },
  });
};

/** Query: Fetch vocabulary quiz result */
export const useStudyVocabQuizResultQuery = (quizSessionId: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.VOCABULARY_QUIZ_RESULT, quizSessionId],
    queryFn: () => studyService.getVocabularyQuizResult(quizSessionId!),
    enabled: !!quizSessionId,
  });
};

/** Mutation: Skip level assessment (bypass diagnosis quiz) — invalidates recommendation cache */
export const useSkipLevelAssessmentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subjectId }: { subjectId: string }) => studyService.skipLevelAssessment(subjectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY.RECOMMENDATION] });
    },
  });
};

/* ── Subject Quiz Practice APIs ── */

/** Mutation: Create subject quiz practice session (all questions upfront) */
export const useCreateSubjectQuizPracticeSessionMutation = () => {
  return useMutation({
    mutationFn: ({ subjectId, level, limit }: { subjectId: string; level: string; limit?: number }) =>
      studyService.createSubjectQuizPracticeSession(subjectId, level, limit),
  });
};

/** Query: Fetch subject quiz practice result */
export const useSubjectQuizPracticeResultQuery = (quizSessionId: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.SUBJECT_QUIZ_PRACTICE_RESULT, quizSessionId],
    queryFn: () => studyService.getSubjectQuizPracticeResult(quizSessionId!),
    enabled: !!quizSessionId,
  });
};

/* ── Boost Message ── */

/** Query: Fetch boost/encouragement message */
export const useBoostMessageQuery = (enabled: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEYS.STUDY.BOOST_MESSAGE],
    queryFn: () => studyService.getBoostMessage(),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
};

/** Mutation: Submit subject quiz practice answer (returns feedback + next question) — invalidates recommendation cache on completion */
export const useSubmitSubjectQuizPracticeAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quizSessionId, req }: { quizSessionId: string; req: TSubjectQuizPracticeAnswerRequest }) =>
      studyService.submitSubjectQuizPracticeAnswer(quizSessionId, req),
    onSuccess: (data) => {
      if (data.isCompleted) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY.RECOMMENDATION] });
      }
    },
  });
};
